/// <reference lib="webworker" />

import type { PrecacheEntry, SerwistGlobalConfig } from 'serwist'
import {
  Serwist,
  NetworkOnly,
  NetworkFirst,
  CacheFirst,
  ExpirationPlugin,
} from 'serwist'

declare global {
  interface WorkerGlobalScope extends SerwistGlobalConfig {
    __SW_MANIFEST: (PrecacheEntry | string)[] | undefined
  }
}

declare const self: ServiceWorkerGlobalScope

// Background Sync API types (not yet in standard TS lib)
interface SyncEvent extends ExtendableEvent {
  readonly tag: string
  readonly lastChance: boolean
}

// ---------------------------------------------------------------------------
// Runtime caching rules — explicit and minimal.
//
// Philosophy: The app has its own caching layer (TanStack Query + IndexedDB).
// The service worker's job is:
//   1. Serve the app shell offline (precache + document fallback)
//   2. Cache specific external assets for offline reading (fonts, themes)
//   3. Stay out of the way for everything else
//
// We intentionally do NOT spread Serwist's `defaultCache`, which applies
// StaleWhileRevalidate to all JS/CSS/images (generating a background network
// fetch for every cached resource on every page load) and NetworkFirst to
// all API/RSC/document/cross-origin requests. That combination caused an
// explosion of edge requests on Vercel — every cached asset doubled its
// request count, every navigation triggered dozens of background fetches.
// ---------------------------------------------------------------------------
const runtimeCaching = [
  // ── Bypass: things that should never touch the Cache API ──────────────

  // Feed proxy — data goes to IndexedDB, never Cache API
  {
    matcher: ({ url }: { url: URL }) => url.pathname.startsWith('/api/proxy'),
    handler: new NetworkOnly(),
  },
  // Supabase — auth + sync handled by HybridAdapter
  {
    matcher: ({ url }: { url: URL }) => url.hostname.includes('supabase.co'),
    handler: new NetworkOnly(),
  },
  // Umami analytics — fire-and-forget
  {
    matcher: ({ url }: { url: URL }) =>
      url.hostname.includes('cloud.umami.is') ||
      url.hostname.includes('pkmn.blogsareback.com') ||
      url.pathname.startsWith('/api/send'),
    handler: new NetworkOnly(),
  },
  // All other API routes — let TanStack Query handle caching;
  // the SW should not cache API responses (and must not cache auth routes)
  {
    matcher: ({ url, sameOrigin }: { url: URL; sameOrigin: boolean }) =>
      sameOrigin && url.pathname.startsWith('/api/'),
    handler: new NetworkOnly(),
  },

  // ── Cache: external assets needed for offline reading ─────────────────

  // Google Fonts CSS — cache for offline, revalidate in background
  // (CSS can change when Google updates font hinting, so not CacheFirst)
  {
    matcher: ({ url }: { url: URL }) =>
      url.hostname === 'fonts.googleapis.com',
    handler: new CacheFirst({
      cacheName: 'google-fonts-stylesheets',
      plugins: [
        new ExpirationPlugin({
          maxEntries: 20,
          maxAgeSeconds: 30 * 24 * 60 * 60, // 30 days
        }),
      ],
    }),
  },
  // Google Fonts files (woff2) — immutable, cache forever
  {
    matcher: ({ url }: { url: URL }) =>
      url.hostname === 'fonts.gstatic.com',
    handler: new CacheFirst({
      cacheName: 'google-fonts-webfonts',
      plugins: [
        new ExpirationPlugin({
          maxEntries: 30,
          maxAgeSeconds: 365 * 24 * 60 * 60, // 1 year
        }),
      ],
    }),
  },
  // CDN assets (highlight.js theme CSS + runtime JS) — versioned URLs, cache-first
  {
    matcher: ({ url }: { url: URL }) =>
      url.hostname === 'cdnjs.cloudflare.com' &&
      (url.pathname.endsWith('.css') || url.pathname.endsWith('.js')),
    handler: new CacheFirst({
      cacheName: 'cdn-styles',
      plugins: [
        new ExpirationPlugin({
          maxEntries: 15,
          maxAgeSeconds: 30 * 24 * 60 * 60, // 30 days
        }),
      ],
    }),
  },
  // Self-hosted syntax highlighting CSS (catppuccin themes in /styles/)
  {
    matcher: ({ url, sameOrigin }: { url: URL; sameOrigin: boolean }) =>
      sameOrigin && url.pathname.startsWith('/styles/syntax-highlighting/'),
    handler: new CacheFirst({
      cacheName: 'syntax-theme-styles',
      plugins: [
        new ExpirationPlugin({
          maxEntries: 10,
          maxAgeSeconds: 30 * 24 * 60 * 60, // 30 days
        }),
      ],
    }),
  },

  // ── Cache: static same-origin assets ────────────────────────────────

  // Icons, favicon, manifest, og-image — rarely change, cache aggressively.
  // Without this rule, the catch-all NetworkOnly sends every icon/manifest
  // request to the edge on every page load (~13 requests per navigation).
  {
    matcher: ({ url, sameOrigin }: { url: URL; sameOrigin: boolean }) =>
      sameOrigin &&
      (url.pathname.startsWith('/icon/') ||
        url.pathname === '/apple-touch-icon.png' ||
        url.pathname === '/favicon.ico' ||
        url.pathname === '/manifest.webmanifest' ||
        url.pathname === '/og-image.jpg'),
    handler: new CacheFirst({
      cacheName: 'static-assets',
      plugins: [
        new ExpirationPlugin({
          maxEntries: 40,
          maxAgeSeconds: 30 * 24 * 60 * 60, // 30 days
        }),
      ],
    }),
  },

  // /_next/static/* — content-hashed, immutable. Cache forever (until
  // a new deployment generates new hashes and the old entries expire).
  // maxEntries must exceed the total chunk count (~200 files) to avoid
  // LRU eviction thrashing that forces re-downloads from the edge.
  {
    matcher: ({ url, sameOrigin }: { url: URL; sameOrigin: boolean }) =>
      sameOrigin && url.pathname.startsWith('/_next/static/'),
    handler: new CacheFirst({
      cacheName: 'next-static',
      plugins: [
        new ExpirationPlugin({
          maxEntries: 300,
          maxAgeSeconds: 30 * 24 * 60 * 60, // 30 days
        }),
      ],
    }),
  },

  // ── Navigation: network-first so we get fresh pages, with offline
  //    fallback to the precached /dashboard shell ────────────────────────

  {
    matcher: ({ request }: { request: Request }) =>
      request.mode === 'navigate',
    handler: new NetworkFirst({
      cacheName: 'pages',
      networkTimeoutSeconds: 5,
      plugins: [
        new ExpirationPlugin({
          maxEntries: 32,
          maxAgeSeconds: 24 * 60 * 60, // 1 day
        }),
      ],
    }),
  },

  // ── Everything else (same-origin only): pass through without caching ──
  // This is the critical difference from defaultCache — we don't cache
  // random JS, CSS, images, or RSC payloads. The browser's HTTP cache
  // handles those just fine.
  //
  // Cross-origin requests that don't match explicit rules above are NOT
  // intercepted — the browser handles them natively. This is essential
  // for direct feed fetches (requires_proxy === false) where re-issuing
  // the request through the SW's fetch() breaks CORS behavior.
  {
    matcher: ({ sameOrigin }: { sameOrigin: boolean }) => sameOrigin,
    handler: new NetworkOnly(),
  },
]

const serwist = new Serwist({
  precacheEntries: self.__SW_MANIFEST,
  skipWaiting: true,
  clientsClaim: true,
  navigationPreload: true,
  runtimeCaching,
  fallbacks: {
    entries: [
      {
        url: '/dashboard',
        matcher({ request }) {
          return request.destination === 'document'
        },
      },
    ],
  },
})

// Helper for identifying analytics beacons that should be swallowed offline.
function isUmamiRequest(url: URL) {
  return (
    url.hostname.includes('cloud.umami.is') ||
    url.hostname.includes('pkmn.blogsareback.com') ||
    url.pathname.startsWith('/api/send')
  )
}

// When offline, short-circuit requests: serve from cache or fail immediately.
// This prevents futile network attempts (e.g. CacheFirst falling through to
// network on cache miss) that pile up as duplicate errors in DevTools.
self.addEventListener('fetch', (event) => {
  if (!navigator.onLine) {
    const url = new URL(event.request.url)

    // Umami analytics — swallow silently
    if (isUmamiRequest(url)) {
      event.respondWith(new Response(null, { status: 200 }))
      return
    }

    // Same-origin non-navigation requests — cache hit → serve, miss → fail fast.
    // Navigation requests pass through to Serwist for /dashboard fallback.
    if (url.origin === self.location.origin && event.request.mode !== 'navigate') {
      event.respondWith(
        caches.match(event.request).then((cached) => cached || Response.error())
      )
      return
    }
  }
})

// ---------------------------------------------------------------------------
// Background Sync API (progressive enhancement)
//
// When the app queues sync operations, it asks the SW to register a sync tag.
// When connectivity returns, the browser fires a 'sync' event. The SW then
// pokes an open client to flush the queue — keeping all business logic in
// the main thread (no Supabase auth needed in SW context).
// ---------------------------------------------------------------------------
const SYNC_TAG = 'bab-sync-queue'

self.addEventListener('sync', (event: SyncEvent) => {
  if (event.tag !== SYNC_TAG) return
  event.waitUntil(
    self.clients.matchAll({ type: 'window' }).then(clients => {
      if (clients.length > 0) {
        const target = clients.find(c => c.visibilityState === 'visible') || clients[0]
        target.postMessage({ type: 'SYNC_QUEUE_FLUSH' })
      }
      // No clients open = items persist in IDB, sync on next visit
    })
  )
})

self.addEventListener('message', (event) => {
  if (event.data?.type === 'REGISTER_SYNC') {
    event.waitUntil(
      (async () => {
        if ('sync' in self.registration) {
          await (self.registration as any).sync.register(SYNC_TAG).catch(() => {})
        }
      })()
    )
  }
})

serwist.addEventListeners()
